import { useEffect, useState, useCallback } from "react";

/**
 * Theme hook — silently follows OS `prefers-color-scheme`.
 * - No UI toggle rendered anywhere
 * - No localStorage: always mirrors the OS setting
 * - Live-updates if the OS switches modes while the page is open
 * - Applies `.dark` class on <html> and syncs <meta name="theme-color">
 */
export const useTheme = () => {
  const detect = () => {
    if (typeof window === "undefined") return "light";
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  };

  const [theme, setTheme] = useState(detect);

  useEffect(() => {
    const mql = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mql) return;
    const onChange = (e) => setTheme(e.matches ? "dark" : "light");
    mql.addEventListener?.("change", onChange);
    return () => mql.removeEventListener?.("change", onChange);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", theme === "dark");
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", theme === "dark" ? "#0D0F14" : "#FFFFFF");
  }, [theme]);

  // Kept for backwards compat but a no-op since there's no toggle.
  const toggle = useCallback(() => {}, []);

  return { theme, toggle };
};
